import { useState, useEffect } from 'react';
import moment from 'moment';
import { fetchQuarterReportDates } from '@user-center/services/quarterReport';
import { QUARTER_OPTIONS_FALLBACK, DEFAULT_QUARTER_FALLBACK } from '../constants';

const buildQuarterOptions = (
  datesResponse: IQuarterDatesResponse | null,
): IQuarterOption[] => {
  if (!datesResponse?.all_dates || datesResponse.all_dates.length === 0) {
    return [];
  }

  // Only show quarters that exist in all_dates (system has generated them)
  return datesResponse.all_dates.map((date) => {
    const dateM = moment(date);
    const quarter = `Q${dateM.quarter()}`;
    // Available if date exists in BOTH all_dates and user_dates
    const available = datesResponse.user_dates?.includes(date) ?? false;

    return {
      quarter,
      date,
      available,
      label: `${dateM.format('YYYY')} ${quarter}`,
    };
  });
};

interface IUseQuarterAvailabilityResult {
  loading: boolean;
  options: IQuarterOption[];
  defaultDate: string; // Date of most recent available quarter
  error: Error | null;
}

const useQuarterAvailability = (openId?: string): IUseQuarterAvailabilityResult => {
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState<IQuarterOption[]>([]);
  const [defaultDate, setDefaultDate] = useState('');
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetchQuarterReportDates({ open_id: openId });

        if (response?.code === 0 && response?.data) {
          const quarterOptions = buildQuarterOptions(response.data);
          setOptions(quarterOptions);

          // Default to most recent quarter in user_dates (user has data for it)
          const userDates = response.data.user_dates || [];
          if (userDates.length > 0) {
            // user_dates is sorted chronologically, so take the last one (most recent)
            setDefaultDate(userDates[userDates.length - 1]);
          } else {
            // Fallback to most recent in all_dates if user has no data yet
            const allDates = response.data.all_dates || [];
            if (allDates.length > 0) {
              setDefaultDate(allDates[allDates.length - 1]);
            }
          }
        } else {
          console.error('Invalid response from quarter_report/dates API');
          setOptions(QUARTER_OPTIONS_FALLBACK);
          setDefaultDate(DEFAULT_QUARTER_FALLBACK);
        }
      } catch (err) {
        console.error('Failed to fetch quarter availability:', err);
        setError(err as Error);
        setOptions(QUARTER_OPTIONS_FALLBACK);
        setDefaultDate(DEFAULT_QUARTER_FALLBACK);
      } finally {
        setLoading(false);
      }
    };

    fetchAvailability();
  }, [openId]);

  return { loading, options, defaultDate, error };
};

export default useQuarterAvailability;
